import { useState } from "react";
import { useAppMode } from "@/context/AppModeContext";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Facebook, Twitter, Instagram, Send, MessageCircle, Mail, Smartphone } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

export default function ShareTiles({ link }) {
  const { appName } = useAppMode();
  const { t: tr } = useLocale();
  const [template, setTemplate] = useState("invite");
  const [custom, setCustom] = useState("");

  const messages = {
    invite: tr("share.inviteMsg").replace("{app}", appName),
    dream: tr("share.dreamMsg").replace("{app}", appName),
  };
  const baseMsg = custom.trim() || messages[template];
  const text = `${baseMsg} ${link}`;
  const encodedText = encodeURIComponent(text);
  const encodedLink = encodeURIComponent(link);

  const pickTemplate = (t) => {
    setTemplate(t);
    setCustom("");
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    toast.success(tr("share.copied"));
  };

  const tiles = [
    { name: "WhatsApp", icon: MessageCircle, color: "text-green-400", href: `https://wa.me/?text=${encodedText}` },
    { name: "Facebook", icon: Facebook, color: "text-blue-400", href: `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}&quote=${encodedText}` },
    { name: "X", icon: Twitter, color: "text-sky-400", href: `https://twitter.com/intent/tweet?text=${encodedText}` },
    { name: "Telegram", icon: Send, color: "text-cyan-400", href: `https://t.me/share/url?url=${encodedLink}&text=${encodeURIComponent(baseMsg)}` },
    {
      name: "Instagram",
      icon: Instagram,
      color: "text-pink-400",
      onClick: async () => {
        await copyText();
        window.open("https://www.instagram.com/", "_blank", "noopener");
      },
    },
    { name: "Email", icon: Mail, color: "text-amber-400", href: `mailto:?subject=${encodeURIComponent(template === "dream" ? `A Dream Date invite 💘` : `Join me on ${appName}`)}&body=${encodedText}` },
    { name: "SMS", icon: Smartphone, color: "text-violet-400", href: `sms:?&body=${encodedText}` },
  ];

  return (
    <div className="mb-4" data-testid="share-tiles">
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={() => pickTemplate("invite")}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            template === "invite" ? "bg-primary/15 border-primary/40 text-primary" : "border-white/10 text-muted-foreground hover:text-white"
          }`}
          data-testid="share-template-invite"
        >
          {tr("share.inviteBtn")}
        </button>
        <button
          type="button"
          onClick={() => pickTemplate("dream")}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            template === "dream" ? "bg-rose-500/15 border-rose-400/40 text-rose-400" : "border-white/10 text-muted-foreground hover:text-white"
          }`}
          data-testid="share-template-dream"
        >
          {tr("share.dreamBtn")}
        </button>
      </div>

      <Textarea
        value={custom}
        onChange={(e) => setCustom(e.target.value)}
        placeholder={messages[template]}
        rows={2}
        maxLength={280}
        className="mb-3 bg-white/5 border-white/10 text-sm"
        data-testid="share-message-input"
      />
      <p className="text-[11px] text-muted-foreground -mt-1.5 mb-3 px-1">
        {tr("share.hint")}
      </p>

      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
        {tiles.map((t) =>
          t.href ? (
            <a
              key={t.name}
              href={t.href}
              target="_blank"
              rel="noreferrer"
              className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:scale-105 transition-all"
              data-testid={`share-tile-${t.name.toLowerCase()}`}
            >
              <t.icon className={`w-5 h-5 ${t.color}`} />
              <span className="text-[10px] text-muted-foreground">{t.name}</span>
            </a>
          ) : (
            <button
              key={t.name}
              type="button"
              onClick={t.onClick}
              className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:scale-105 transition-all"
              data-testid={`share-tile-${t.name.toLowerCase()}`}
            >
              <t.icon className={`w-5 h-5 ${t.color}`} />
              <span className="text-[10px] text-muted-foreground">{t.name}</span>
            </button>
          )
        )}
      </div>
    </div>
  );
}
